import {useCallback, useRef} from 'react';
import {createVariableSpanElement} from '../utils/variableUtils';
import {getRangeInElement, getCursorOffsetInElement, insertWithSpacing} from '../utils/dom';
import {TemplateStructure, TemplateItem} from '../types/templateStructure';
import {getUpdatedItemFromElement} from '../utils/itemUpdateUtils';

interface UseVariableInsertionProps {
    structure: TemplateStructure;
    defaultVariables: Record<string, string>;
    onItemChange: (index: number, updatedItem: TemplateItem) => void;
}

/**
 * Hook for variable insertion with DOM manipulation
 * Determines insertion target based on currently focused element
 */
export function useVariableInsertion({
    structure,
    defaultVariables,
    onItemChange
}: UseVariableInsertionProps) {
    const lastFocusedEditableRef = useRef<HTMLElement | null>(null);
    const lastFocusedCursorOffsetRef = useRef<number | null>(null);

    const insertVariableIntoElement = useCallback((element: HTMLElement, variableKey: string) => {
        const variable = defaultVariables[variableKey];
        if(!variable) return;

        const selection = window.getSelection();
        if(!selection) return;

        const savedOffset = element === lastFocusedEditableRef.current ? lastFocusedCursorOffsetRef.current : null;
        const range = getRangeInElement(element, savedOffset);

        selection.removeAllRanges();
        selection.addRange(range);

        const variableSpan = createVariableSpanElement(variableKey, variable);
        insertWithSpacing(element, range, variableSpan, selection);
    }, [defaultVariables]);

    const updateItemContent = useCallback((structureIndex: number, item: TemplateItem, target: HTMLElement) => {
        const updatedItem = getUpdatedItemFromElement(item, target);
        
        if(updatedItem) {
            onItemChange(structureIndex, updatedItem);
        }
    }, [onItemChange]);

    const handleInsertVariable = useCallback((variableKey: string) => {
        const activeElement = document.activeElement as HTMLElement;
        const target = activeElement?.isContentEditable ? activeElement : lastFocusedEditableRef.current;
        if(!target?.isContentEditable) return;
        
        const structureIndexAttr = target.getAttribute('data-structure-index');
        if(structureIndexAttr === null) return;
        
        insertVariableIntoElement(target, variableKey);
        
        const structureIndex = parseInt(structureIndexAttr, 10);
        const item = structure[structureIndex];
        
        if(!item) return;
        
        updateItemContent(structureIndex, item, target);
    }, [structure, insertVariableIntoElement, updateItemContent]);

    const setLastFocusedEditable = useCallback((element: HTMLElement | null) => {
        lastFocusedEditableRef.current = element;
        if(element) {
            lastFocusedCursorOffsetRef.current = getCursorOffsetInElement(element);
        } else {
            lastFocusedCursorOffsetRef.current = null;
        }
    }, []);

    const updateSavedCursorOffset = useCallback(() => {
        const el = lastFocusedEditableRef.current;
        if(el && document.activeElement === el) {
            lastFocusedCursorOffsetRef.current = getCursorOffsetInElement(el);
        }
    }, []);

    return {handleInsertVariable, setLastFocusedEditable, updateSavedCursorOffset};
}
